import { createFileRoute } from "@tanstack/react-router";
import { ServiceDetail } from "@/components/ServiceDetail";
import { NeighborhoodLinks } from "@/components/NeighborhoodLinks";
import { serviceGraph, abs } from "@/lib/serviceSchema";
import img from "@/assets/Apex_Theater.webp.asset.json";
import img800 from "@/assets/Apex_Theater.webp.asset.json";

import { serviceAreas } from "@/data/serviceAreas";

const faqs = [
  {
    q: "How much does a custom home theater cost in Denver?",
    a: "Dedicated theater rooms typically start around $25,000 for a 4K projector, immersive surround, motorized screen, and basic acoustic treatment. Reference-grade builds with Dolby Atmos, tiered seating, starfield ceilings, and full acoustic engineering generally run $60,000–$250,000+. We scope and price every project up front before any work starts.",
  },
  {
    q: "Do I need a dedicated room, or can you do a media room?",
    a: "Both. Dedicated theaters give you the best picture, sound isolation, and acoustic control. Media rooms (open to the kitchen or living area) can still get you reference-grade audio with hidden in-wall speakers, motorized screens, and one-touch lighting and shading scenes.",
  },
  {
    q: "What's the difference between a soundbar and real surround sound?",
    a: "A soundbar is two to five small drivers in one box — fine for casual TV. A real installed surround system uses 7 to 11+ discrete speakers (plus subs and overhead Atmos channels) calibrated to your room. Dialogue is clearer, effects move around you, and bass actually moves the seats.",
  },
  {
    q: "Can you treat the room acoustically without making it look like a recording studio?",
    a: "Yes. We use acoustically transparent fabric walls, designer panels, and engineered ceilings that hide treatment behind finishes you'd actually pick for the rest of the house. The room sounds like a reference theater and looks like part of the home.",
  },
  {
    q: "Do you serve all of the Denver Metro Area?",
    a: "Yes — Denver, Cherry Creek, Highlands Ranch, Castle Rock, Castle Pines, Boulder, Parker, Littleton, and the rest of the Front Range. Most theater projects begin with a free in-home design consultation.",
  },
];

const testimonials = [
  { quote: "They designed a 9.4.6 Atmos theater in our basement that genuinely sounds better than the local Cinemark. Acoustic panels are invisible behind the fabric walls.", name: "Mark T.", location: "Castle Pines" },
  { quote: "From sightline math to seat layout to the starfield ceiling — every detail thought through. One button drops the screen, dims lights, fires the projector. Magic.", name: "Sarah L.", location: "Cherry Creek" },
  { quote: "Apex did our media room with hidden in-walls and a 120\" screen. Looks like a normal great room until movie night.", name: "Jordan B.", location: "Highlands Ranch" },
];

export const Route = createFileRoute("/services/home-theater")({
  head: () => ({
    meta: [
      { title: "Custom Home Theater Design & Install | Apex AV Denver" },
      { name: "description", content: "Reference-grade home theater design & install in Denver from $25K — Dolby Atmos, 4K projection, acoustic treatment. Free design consultation." },
      { name: "keywords", content: "custom home theater Denver, home theater installation Denver, home theater design Denver, dedicated theater room Denver, surround sound installation Denver, Dolby Atmos home theater Denver, 4K projector installation Denver, 8K projector installation Denver, motorized projector screen Denver, acoustic treatment Denver, theater seating Denver, media room installation Denver" },
      { property: "og:title", content: "Custom Home Theater — Apex AV Denver" },
      { property: "og:description", content: "Dedicated immersive theater rooms designed and installed across Denver. Free design consultation — from $25,000." },
      { property: "og:image", content: abs(img.url) },
      { property: "og:image:alt", content: "Apex AV custom home theater installation in Denver, Colorado" },
      { name: "twitter:image", content: abs(img.url) },
      { property: "og:url", content: "https://apexav.com/services/home-theater" },
      { property: "og:type", content: "website" },
      { name: "twitter:title", content: "Custom Home Theater — Apex AV Denver" },
      { name: "twitter:description", content: "Dedicated immersive theater rooms designed and installed across Denver. Free design consultation — from $25,000." },
    ],
    links: [{ rel: "canonical", href: "https://apexav.com/services/home-theater" }, { rel: "preload", as: "image", href: img800.url, fetchPriority: "high" } as never],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(
          serviceGraph({
            slug: "home-theater",
            name: "Custom Home Theater Installation",
            description: "Reference-grade dedicated home theater design and installation in Denver. Starting at $25,000.",
            pageTitle: "Custom Home Theater Design & Install | Apex AV Denver",
            pageDescription: "Reference-grade home theater design & install in Denver from $25K — Dolby Atmos, 4K projection, acoustic treatment. Free design consultation.",
            image: img.url,
            faqs,
            serviceType: "Custom Home Theater Installation",
            minPrice: 25000,
            priceDescription: "Entry-level dedicated theater with 4K projection, immersive surround, and motorized screen. Reference-grade Atmos builds with full acoustic engineering typically run $60K–$250K+ with a fixed scope up front.",
            offerCatalog: [
              "Object-based immersive surround design and calibration",
              "4K and 8K laser projection with motorized screens",
              "Acoustic treatment, soundproofing, and room calibration",
              "Tiered seating, fiber-optic starfield ceilings, sconces",
              "Integrated lighting, shading, and one-touch scenes",
              "Industry-certified design and engineering team",
            ],
            breadcrumbName: "Custom Home Theater",
            extraNodes: [
              {
                "@type": "VideoObject",
                name: "Custom Home Theater Design & Install — Apex AV Denver",
                description: "Reference-grade home theater design and installation in Denver. Dedicated theater rooms with immersive surround sound, 4K projection, and acoustic treatment.",
                thumbnailUrl: ["https://apexav.com/videos/hero-theater.jpg"],
                uploadDate: "2026-06-01T00:00:00-06:00",
                contentUrl: "https://apexav.com/videos/home-theater-video.mp4",
                embedUrl: "https://apexav.com/services/home-theater",
              },
            ],
          })
        ),
      },
    ],
  }),
  component: () => (
    <>
    <ServiceDetail
      topics={["home-theater", "dolby-atmos", "whole-home-audio", "av-installation", "lighting", "prewire", "whole-home-video"]}
      eyebrow="Home theater"
      title="A theater built around you."
      intro={<>Dedicated theater rooms designed acoustically and visually for reference-grade performance. From seat layout and sightlines to <strong>immersive surround calibration</strong>, <strong>home theater installation in Denver</strong>, and <strong>acoustic treatment</strong>, we design every detail around how your family actually watches.</>}
      image={img800.url}
      imageAlt="Custom immersive Dolby Atmos home theater installation in a Cherry Creek Denver home"
      brands={["sony", "lg", "samsung", "marantz", "denon", "klipsch", "episode", "sanus"]}
      video="/videos/home-theater-video.mp4"
      bullets={[
        "Object-based immersive surround design and calibration",
        "4K and 8K laser projection with motorized screens",
        "Acoustic treatment, soundproofing, and room calibration",
        "Tiered seating, fiber-optic starfield ceilings, sconces",
        "Integrated lighting, shading, and one-touch scenes",
        "Industry-certified design and engineering team",
      ]}
      features={[
        { title: "Reference performance", desc: "Calibrated to industry standards — what the director actually intended." },
        { title: "Designed to disappear", desc: "Speakers, wires, and equipment hidden behind acoustically transparent finishes." },
        { title: "One-tap movie night", desc: "Lights dim, shades drop, projector fires — all from a single button." },
      ]}
      startingPrice="$25,000"
      priceNote="Entry-level dedicated theater with 4K projection, immersive surround, and motorized screen. Reference-grade Atmos builds with full acoustic engineering typically run $60K–$250K+ with a fixed scope up front."
      testimonials={testimonials}
      faqs={faqs}
      serviceAreas={serviceAreas.map((a) => ({ slug: a.slug, name: a.name }))}
      relatedServices={[
        { to: "/services/dolby-atmos-home-theater", label: "Dolby Atmos Installation", desc: "A focused guide to immersive speaker layouts, overhead channels, bass integration, and calibration." },
        { to: "/services/smart-home", label: "Smart Home Automation", desc: "One interface to dim lights, drop the screen, and start the movie." },
        { to: "/services/whole-home-audio", label: "Whole-Home Audio", desc: "Extend theater-grade sound into every room of the house." },
        { to: "/services/hidden-audio", label: "Hidden & Invisible Audio", desc: "Plaster-over speakers for rooms where nothing should be visible." },
        { to: "/services/lighting", label: "Smart Lighting", desc: "Tunable scenes that drop instantly for movie night." },
        { to: "/services/shading", label: "Motorized Shading", desc: "Whisper-quiet shades that close on cue with the projector." },
        { to: "/services/prewire", label: "New Construction Prewire", desc: "Design the theater into the framing — half the cost of a retrofit." },
      ]}
    />
    <NeighborhoodLinks
      service="home-theater"
      heading="Home theater installation by neighborhood"
      subheading="Hyper-local design notes for the homes, basements, and HOAs we work in most often."
    />
    </>
  ),
});
